/**
 * Sprint 15 C.2 (2026-05-10): CSP nonce migration для index.html.
 *
 * Этот модуль заменяет ~57 inline onclick/onchange/oninput атрибутов
 * в index.html через event delegation на data-action="...".
 *
 * Подход:
 *   - Один click/change/input listener на document.
 *   - Каждый элемент с data-action="..." получает свой handler из карты ниже.
 *   - Параметры handler'а передаются через data-arg / data-* attributes.
 *
 * Результат: index.html не содержит inline JS → можно переключить
 * STRICT CSP (script-src-attr: 'none') в server.js.
 *
 * ВНИМАНИЕ: все handler'ы зависят от `window.QuoteCalc`. Этот глобальный
 * объект создаётся в init.js после загрузки entry.js. Поэтому здесь мы
 * НЕ обращаемся к нему на верхнем уровне — только внутри runtime handler'ов
 * (на момент клика QuoteCalc уже инициализирован).
 */

(function () {
    'use strict';

    function getCalc() {
        return window.QuoteCalc || null;
    }

    /**
     * Карта data-action → функция.
     * Каждая функция получает (element, event) и сама извлекает
     * нужные параметры из dataset / event.target / event.
     */
    const CLICK_ACTIONS = {
        // ─── Mode switcher ───
        'switch-mode-quote': () => getCalc()?.switchMode('quote'),
        'switch-mode-checklist': () => getCalc()?.switchMode('checklist'),

        // ─── Catalog / categories ───
        'toggle-categories': () => getCalc()?.toggleCategories(),
        'toggle-search-expand': () => getCalc()?.toggleSearchExpand(),
        'show-catalog-modal': () => getCalc()?.showCatalogModal(),
        'hide-catalog-modal': () => getCalc()?.hideCatalogModal(),
        'duplicate-current-template': () => getCalc()?.duplicateCurrentTemplate(),
        'add-to-catalog': () => getCalc()?.addToCatalog(),
        'create-supplier-inline': () => getCalc()?.createSupplierInline(),
        'save-catalog-json': () => getCalc()?.saveCatalogJSON(),

        // ─── Quote builder ───
        'toggle-quote-comments': () => getCalc()?.toggleQuoteComments(),
        'toggle-program-description': () => getCalc()?.toggleProgramDescription(),
        'show-service-modal': () => getCalc()?.showServiceModal(),
        'hide-service-modal': () => getCalc()?.hideServiceModal(),
        'add-custom-service': () => getCalc()?.addCustomService(),
        'show-add-hotel-modal': () => getCalc()?.showAddHotelModal(),
        'show-add-flight-modal': () => getCalc()?.showAddFlightModal(),
        'show-add-other-service-modal': () => getCalc()?.showAddOtherServiceModal(),
        'show-optional-service-modal': () => getCalc()?.showOptionalServiceModal(),

        // ─── Services list controls ───
        'toggle-sort-direction': () => getCalc()?.toggleSortDirection(),

        // ─── Quick filter (checklist) ───
        'set-checklist-quick-filter': (el) => {
            const filter = el.dataset.filter || el.dataset.qfilter || 'all';
            getCalc()?.setChecklistQuickFilter(filter);
        },

        // ─── Print / send ───
        'print': () => getCalc()?.print(),
        'print-v2': () => getCalc()?.printV2(),
        'send-email': () => getCalc()?.sendEmail(),

        // ─── Category modal ───
        'hide-category-modal': () => getCalc()?.hideCategoryModal(),
        'add-category': () => getCalc()?.addCategory(),

        // ─── Categories manager (settings) ───
        'hide-categories-manager': () => getCalc()?.hideCategoriesManager(),
        'switch-settings-tab': (el) => {
            const tab = el.dataset.tab || el.dataset.settingsTab || 'main';
            getCalc()?.switchSettingsTab(tab);
        },
        'add-region-from-manager': () => getCalc()?.addRegionFromManager(),
        'add-category-from-manager': () => getCalc()?.addCategoryFromManager(),
        'manual-reorganize-categories': () => getCalc()?.manualReorganizeCategories(),
        'reset-category-stats': () => getCalc()?.resetCategoryStats(),
        'manual-reorganize-templates': () => getCalc()?.manualReorganizeTemplates(),
        'reset-template-stats': () => getCalc()?.resetTemplateStats(),

        // ─── Export / import ───
        'handle-export-all': () => getCalc()?.handleExportAll(),
        'handle-export-database': () => getCalc()?.handleExportDatabase(),

        // ─── Helpers: trigger hidden file input ───
        'trigger-file-input': (el) => {
            const targetId = el.dataset.target;
            if (!targetId) return;
            const input = document.getElementById(targetId);
            if (input) input.click();
        },

        // ─── Search input — preserve event.stopPropagation() behavior ───
        // Этот action — no-op, нам нужен только stopPropagation, что
        // делается отдельно ниже (см. SEARCH_INPUT_STOP_PROP).
        'search-input-stop': () => { /* handled in delegation */ }
    };

    const CHANGE_ACTIONS = {
        'load-quote-file': (_, event) => getCalc()?.loadQuoteFile(event),
        'load-catalog-file': (_, event) => getCalc()?.loadCatalogFile(event),
        'handle-import-all': (_, event) => getCalc()?.handleImportAll(event),
        'handle-import-database': (_, event) => getCalc()?.handleImportDatabase(event),
        'change-region': (el) => getCalc()?.changeRegion(el.value),
        'toggle-view-mode': () => getCalc()?.toggleViewMode(),
        'handle-services-sort-change': () => getCalc()?.handleServicesSortChange()
    };

    const INPUT_ACTIONS = {
        'filter-templates': (el) => getCalc()?.filterTemplates(el.value)
    };

    // ─── Click delegation ───
    document.addEventListener('click', (event) => {
        const target = event.target.closest('[data-action]');
        if (!target) return;
        const action = target.dataset.action;

        // Special case: search input нужно остановить propagation
        // (раньше было onclick="event.stopPropagation()" на input).
        if (action === 'search-input-stop') {
            event.stopPropagation();
            return;
        }

        const handler = CLICK_ACTIONS[action];
        if (handler) {
            handler(target, event);
        }
    });

    // ─── Change delegation (input/select) ───
    document.addEventListener('change', (event) => {
        const target = event.target.closest('[data-change-action]');
        if (!target) return;
        const action = target.dataset.changeAction;
        const handler = CHANGE_ACTIONS[action];
        if (handler) {
            handler(target, event);
        }
    });

    // ─── Input delegation (oninput for typing) ───
    document.addEventListener('input', (event) => {
        const target = event.target.closest('[data-input-action]');
        if (!target) return;
        const action = target.dataset.inputAction;
        const handler = INPUT_ACTIONS[action];
        if (handler) {
            handler(target, event);
        }
    });

    console.log('[inline-handlers] event delegation initialized');
})();
